import React, { useState } from 'react';
import { PersonalInfo } from './sections/PersonalInfo';
import { Experience } from './sections/Experience';
import { Education } from './sections/Education';
import { Skills } from './sections/Skills';
import { Projects } from './sections/Projects';
import { LinkedInImport } from './components/LinkedInImport';
import type { ResumeData, Experience as ExperienceType, Education as EducationType, Project } from './types';
import { motion } from 'framer-motion';

const defaultExperience: Omit<ExperienceType, 'id'> = {
  company: '',
  position: '',
  startDate: '',
  endDate: '',
  description: '',
  isCurrentJob: false,
  location: '',
  achievements: []
};

const defaultProject: Omit<Project, 'id'> = {
  title: '',
  description: '',
  technologies: '',
  startDate: '',
  endDate: '',
  achievements: []
};

export function ResumeForm({ onSubmit }: { onSubmit: (data: ResumeData) => void }) {
  const [experiences, setExperiences] = useState<ExperienceType[]>([{ 
    ...defaultExperience,
    id: 1
  }]);
  
  const [education, setEducation] = useState<EducationType[]>([{
    id: 1,
    institution: '',
    degree: '',
    fieldOfStudy: '',
    startDate: '',
    endDate: '',
    isPursuing: false,
    grade: '',
    achievements: ''
  }]);

  const [projects, setProjects] = useState<Project[]>([{
    ...defaultProject,
    id: 1
  }]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    
    const personalInfo = {
      fullName: formData.get('fullName') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      location: formData.get('location') as string,
      website: formData.get('website') as string,
      linkedin: formData.get('linkedin') as string,
      github: formData.get('github') as string,
      summary: formData.get('summary') as string,
    };

    const skills = {
      technical: formData.get('technical_skills') as string,
      soft: formData.get('soft_skills') as string,
      languages: formData.get('languages') as string,
      tools: formData.get('tools') as string,
      certifications: (formData.get('certifications') as string)?.split(',').map(cert => cert.trim()) || [],
    };

    const resumeData: ResumeData = {
      personalInfo,
      experiences,
      education,
      projects,
      skills,
    };

    onSubmit(resumeData);
  };

  const handleLinkedInImport = (importedData: Partial<ResumeData>) => {
    if (importedData.personalInfo) {
      // Update form fields through refs or state
    }
    if (importedData.experiences) {
      setExperiences(importedData.experiences);
    }
    if (importedData.education) {
      setEducation(importedData.education);
    }
    if (importedData.projects) {
      setProjects(importedData.projects);
    }
    if (importedData.skills) {
      // Update skills fields
    }
  };

  return (
    <motion.form 
      onSubmit={handleSubmit} 
      className="space-y-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <LinkedInImport onImport={handleLinkedInImport} />
      <PersonalInfo />
      <Experience experiences={experiences} setExperiences={setExperiences} />
      <Education education={education} setEducation={setEducation} />
      <Projects projects={projects} setProjects={setProjects} />
      <Skills />
      
      <motion.div 
        className="flex justify-end"
        whileHover={{ scale: 1.02 }}
      >
        <button
          type="submit"
          className="bg-indigo-600 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 
            transition-all duration-200 transform hover:-translate-y-1 hover:shadow-lg
            flex items-center gap-2"
        >
          Generate Resume
        </button>
      </motion.div>
    </motion.form>
  );
}